<template>
  <div class="ccma-address billing-address-screen">
    <div v-if="isLoadingAddresses" class="loading-message">
      {{ t('consultantCorner.address.LoadingAddresses') }}
    </div>

    <div v-else-if="showError" class="ccma-address-error">
      <h2>{{ t('consultantCorner.address.PageLoadError') }}</h2>
      <button class="baseBtn primaryBtn" @click="reloadAddressData">
        {{ t('consultantCorner.address.TryAgain') }}
      </button>
    </div>

    <div v-else-if="showAddressSelection" class="address-selection-view">
      <div class="selection-header">
        <button @click="cancelAddressSelection" class="baseBtn secondaryBtn back-button">
          {{ t('buttons.Back') }}
        </button>
        <h3>
          {{ t('accountInfo.billingAddress.selectAddress') }}
          {{ getAddressTypeLabel(addressSelectionType) }}
        </h3>
      </div>

      <div v-if="addressList.length === 0" class="no-addresses">
        <p>{{ t('consultantCorner.address.NoSavedAddresses') }}</p>
      </div>

      <div v-else class="address-list">
        <div
          v-for="address in addressList"
          :key="address.addressId"
          class="address-item"
        >
          <div class="address-content" @click="selectExistingAddress(address)">
            <div class="address-name">{{ address.firstName }} {{ address.lastName }}</div>
            <div class="address-line">
              {{ address.addressLine1 }}
              <span v-if="address.houseNumber"> {{ address.houseNumber }}</span>
            </div>
            <div v-if="address.addressLine2" class="address-line">{{ address.addressLine2 }}</div>
            <div class="address-line">{{ formatCityStatePostal(address) }}</div>
            <div class="address-line">{{ address.countryCode }}</div>
          </div>

          <div class="address-item-actions">
            <button class="baseBtn secondaryBtn" @click.stop="editExistingAddress(address)">
              {{ t('accountInfo.billingAddress.editAddress') }}
            </button>
            <button
              class="baseBtn outlineBtn"
              @click.stop="deleteAddress(address)"
              :title="t('accountInfo.billingAddress.deleteAddress')"
            >
              {{ t('accountInfo.billingAddress.deleteAddress') }}
            </button>
          </div>
        </div>
      </div>

      <div class="address-item add-new" @click="addNewAddress">
        <div class="add-new-content">
          <span>{{ t('accountInfo.billingAddress.addNewAddress') }}</span>
        </div>
      </div>
    </div>

    <div v-else-if="showAddressForm" class="address-form-view">
      <div class="form-header">
        <button @click="cancelAddressForm" class="baseBtn secondaryBtn back-button">
          {{ t('buttons.Back') }}
        </button>
        <h3>
          {{ editingAddress ? t('accountInfo.billingAddress.editAddress') : t('accountInfo.billingAddress.addNewAddress') }}
        </h3>
      </div>

      <AddressControl
        ref="addressControlRef"
        :name="addressNameDisplay"
        :addressLine1="addressForm.address1"
        :addressLine2="addressForm.address2"
        :houseNumber="addressForm.houseNumber"
        :city="addressForm.city"
        :stateCode="addressForm.state"
        :postalCode="addressForm.postalCode"
        :countryCode="addressForm.country"
        :columnClass="'col-12'"
        :isdisabled="false"
        @address-change="onAddressChange"
      />

      <div class="form-actions">
        <button @click="cancelAddressForm" class="baseBtn secondaryBtn">
          {{ t('buttons.Cancel') }}
        </button>
        <button @click="saveAddress" class="baseBtn primaryBtn" :disabled="isSaving">
          {{ isSaving ? t('consultantCorner.address.Saving') : t('buttons.Save') }}
        </button>
      </div>
    </div>

    <div v-else class="address-management-view">
      <div class="address-header">
        <div class="first">
          <div class="address-title">{{ t('consultantCorner.address.AddressManagement') }}</div>
          <div class="address-subtitle mb-1">{{ t('consultantCorner.address.ManageAddresses') }}</div>
        </div>
      </div>

      <div class="current-addresses">
        <div
          class="address-section"
          v-for="addressType in addressTypes"
          :key="addressType.type"
        >
          <h3 class="address-section-title">{{ addressType.label }}</h3>

          <div v-if="getAddressByType(addressType.type)" class="address-display ccma-address-card-layout">
            <div class="address-content">
              <div class="address-name">
                {{ getAddressByType(addressType.type)?.firstName }}
                {{ getAddressByType(addressType.type)?.lastName }}
              </div>
              <div class="address-line">
                {{ getAddressByType(addressType.type)?.addressLine1 }}
                <span v-if="getAddressByType(addressType.type)?.houseNumber">
                  {{ getAddressByType(addressType.type)?.houseNumber }}
                </span>
              </div>
              <div v-if="getAddressByType(addressType.type)?.addressLine2" class="address-line">
                {{ getAddressByType(addressType.type)?.addressLine2 }}
              </div>
              <div class="address-line">{{ formatCityStatePostal(getAddressByType(addressType.type)) }}</div>
              <div class="address-line">{{ getAddressByType(addressType.type)?.countryCode }}</div>
            </div>

            <div class="address-actions ccma-address-card-actions">
              <button
                @click="changeAddress(addressType.type)"
                class="baseBtn primaryBtn ccma-address-card-action-btn"
              >
                {{ t('consultantCorner.address.Change') }}
              </button>
              <button
                @click="openNewAddressForm(addressType.type)"
                class="baseBtn secondaryBtn ccma-address-card-action-btn"
              >
                {{ t('accountInfo.billingAddress.createNew') }}
              </button>
            </div>
          </div>

          <div v-else class="no-address">
            <p>{{ getNoAddressText(addressType.type) }}</p>
            <button @click="openNewAddressForm(addressType.type)" class="baseBtn primaryBtn">
              {{ getAddAddressButtonText(addressType.type) }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="addressVerificationShow" class="popup-backdrop"></div>
    <Popup
      :show="addressVerificationShow"
      :popup-class="'address-val-popup-content'"
    >
      <VerifyAddressPopup
        :enteredAddress="{
          firstName: addressForm.firstName,
          lastName: addressForm.lastName,
          addressLine1: addressForm.address1,
          addressLine2: addressForm.address2,
          houseNumber: addressForm.houseNumber,
          city: addressForm.city,
          stateCode: addressForm.state,
          postalCode: addressForm.postalCode,
          countryCode: addressForm.country,
        }"
        :verifyAddress="verifyAddress"
        :verifyAddressError="addressVerifyError"
        @use-entered-address="useEnteredAddressClick"
        @use-selected-address="useSelectedAddressClick"
        @re-enter-address="reEnterAddressClick"
      />
    </Popup>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Swal from 'sweetalert2'
import { Popup } from '@progress/kendo-vue-popup'
import AddressControl from '../PWS/AddressControl.vue'
import VerifyAddressPopup from '../VerifyAddressPopup.vue'
import {
  PwsProfileAddressDelete,
  PwsProfileAddressGet,
  PwsProfileAddressSave,
} from '../../api/AccountInfo'
import {
  GetBeeNumberAuthenicatedUser,
  GetCountryCode,
} from '../../stores/SettingsStore'
import { useStoreLogger } from '../../logger'
import { useFormValidation } from '../../composables/useFormValidation'
import { addressSchema } from '../../validators/ValidationSchemas'
import { handleError } from '../../services/ErrorService'
import type { IAddress, IPwsProfileAddressSaveResponse } from '../../types/AccountInfoTypes'
import type { IAddressVerificationListItem } from '../../types/CountryCultureTypes'

type AddressSelectionType = 'billing' | 'shipping' | 'mailing'

interface IAddressChangeEvent {
  addressLine1: string
  addressLine2: string | null
  houseNumber: string | null
  city: string
  stateCode: string | null
  postalCode: string
  countryCode: string
  firstName: string
  lastName: string
}

const { t } = useI18n()
const logger = useStoreLogger()
const { errors: addressErrors, validateAll: validateAddressAll, clearErrors: clearAddressErrors } = useFormValidation()
const addressControlRef = ref<InstanceType<typeof AddressControl> | null>(null)

const showError = ref(false)
const isSaving = ref(false)
const isLoadingAddresses = ref(false)

const showAddressSelection = ref(false)
const showAddressForm = ref(false)
const editingAddress = ref(false)
const addressSelectionType = ref<AddressSelectionType>('billing')
const currentAddressId = ref<number | null>(null)
const pendingSaveAddressId = ref<number | undefined>(undefined)

const addressVerificationShow = ref(false)
const verifyAddress = ref<IAddressVerificationListItem[]>([])
const addressVerifyError = ref(false)

const addressList = ref<IAddress[]>([])
const billingAddress = ref<IAddress | null>(null)
const shippingAddress = ref<IAddress | null>(null)
const mailingAddress = ref<IAddress | null>(null)
const addressNameDisplay = ref('')

const addressForm = ref({
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  houseNumber: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
})

const addressTypes = computed(() => [
  { type: 'billing' as const, label: t('consultantCorner.address.BillingAddress') },
  { type: 'shipping' as const, label: t('consultantCorner.address.DeliveryAddress') },
  { type: 'mailing' as const, label: t('consultantCorner.address.MailingAddress') },
])

function resetAddressForm(countryCode: string = '') {
  addressForm.value = {
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    houseNumber: '',
    city: '',
    state: '',
    postalCode: '',
    country: countryCode,
  }
  addressNameDisplay.value = ''
}

function formatCityStatePostal(address: IAddress | null | undefined): string {
  const city = address?.city ?? ''
  const state = address?.stateCode && address.stateCode !== 'N/A' ? address.stateCode : ''
  const postalCode = address?.postalCode ?? ''
  const cityState = state ? `${city}, ${state}` : city

  return `${cityState} ${postalCode}`.trim()
}

function getAddressByType(type: AddressSelectionType): IAddress | null {
  switch (type) {
    case 'billing':
      return billingAddress.value
    case 'shipping':
      return shippingAddress.value
    case 'mailing':
      return mailingAddress.value
    default:
      return null
  }
}

function getAddressTypeLabel(type: AddressSelectionType): string {
  switch (type) {
    case 'billing':
      return t('consultantCorner.address.BillingAddress')
    case 'shipping':
      return t('consultantCorner.address.DeliveryAddress')
    case 'mailing':
      return t('consultantCorner.address.MailingAddress')
    default:
      return ''
  }
}

function getNoAddressText(type: AddressSelectionType): string {
  switch (type) {
    case 'billing':
      return t('accountInfo.billingAddress.noBillingAddress')
    case 'shipping':
      return t('accountInfo.billingAddress.noShippingAddress')
    case 'mailing':
      return t('consultantCorner.address.NoAddressSet')
    default:
      return t('consultantCorner.address.NoAddressSet')
  }
}

function getAddAddressButtonText(type: AddressSelectionType): string {
  switch (type) {
    case 'billing':
      return t('accountInfo.billingAddress.addBillingAddress')
    case 'shipping':
      return t('accountInfo.billingAddress.addShippingAddress')
    case 'mailing':
      return t('consultantCorner.address.AddNew')
    default:
      return t('consultantCorner.address.AddNew')
  }
}

function isAddressIdSharedAcrossTypes(addressId: number | undefined): boolean {
  if (!addressId) return false

  const addressIds = [
    billingAddress.value?.addressId,
    shippingAddress.value?.addressId,
    mailingAddress.value?.addressId,
  ].filter((id): id is number => typeof id === 'number' && id > 0)

  return addressIds.filter(id => id === addressId).length > 1
}

async function getBeeNumber(): Promise<number | null> {
  const beeNumber = await GetBeeNumberAuthenicatedUser()
  if (!beeNumber) {
    logger.error('No authenticated user bee number found for address action')
    return null
  }
  return beeNumber
}

async function loadAddressList() {
  try {
    isLoadingAddresses.value = true
    showError.value = false

    const beeNumber = await getBeeNumber()
    if (!beeNumber) {
      showError.value = true
      return
    }

    const response = await PwsProfileAddressGet({ beeNumber })

    if (!response?.item) {
      addressList.value = []
      billingAddress.value = null
      shippingAddress.value = null
      mailingAddress.value = null
      return
    }

    const item = response.item
    addressList.value = (item.allAddresses ?? []).map(wrapper => ({
      ...wrapper.address,
      addressId: wrapper.addressId,
    }))

    billingAddress.value = item.billingAddress
      ? { ...item.billingAddress.address, addressId: item.billingAddress.addressId }
      : null
    shippingAddress.value = item.shippingAddress
      ? { ...item.shippingAddress.address, addressId: item.shippingAddress.addressId }
      : null
    mailingAddress.value = item.mailingAddress
      ? { ...item.mailingAddress.address, addressId: item.mailingAddress.addressId }
      : null
  } catch (error) {
    logger.error('Failed to load address list', error)
    showError.value = true
    await handleError(error, t)
  } finally {
    isLoadingAddresses.value = false
  }
}

function changeAddress(type: AddressSelectionType) {
  addressSelectionType.value = type
  showAddressSelection.value = true
}

function cancelAddressSelection() {
  showAddressSelection.value = false
}

async function openNewAddressForm(type: AddressSelectionType) {
  addressSelectionType.value = type
  await addNewAddress()
}

async function addNewAddress() {
  editingAddress.value = false
  currentAddressId.value = null
  pendingSaveAddressId.value = undefined

  const defaultCountry = await GetCountryCode()
  resetAddressForm(defaultCountry)

  showAddressForm.value = true
  showAddressSelection.value = false
}

function editExistingAddress(address: IAddress) {
  editingAddress.value = true
  currentAddressId.value = address.addressId ?? null
  pendingSaveAddressId.value = undefined

  addressForm.value = {
    firstName: address.firstName || '',
    lastName: address.lastName || '',
    address1: address.addressLine1 || '',
    address2: address.addressLine2 || '',
    houseNumber: address.houseNumber || '',
    city: address.city || '',
    state: address.stateCode || '',
    postalCode: address.postalCode || '',
    country: address.countryCode || '',
  }
  addressNameDisplay.value = address.firstName + ' ' + address.lastName

  showAddressForm.value = true
  showAddressSelection.value = false
}

function onAddressChange(updatedAddress: IAddressChangeEvent) {
  addressForm.value = {
    ...addressForm.value,
    firstName: updatedAddress.firstName,
    lastName: updatedAddress.lastName,
    address1: updatedAddress.addressLine1,
    address2: updatedAddress.addressLine2 || '',
    houseNumber: updatedAddress.houseNumber || '',
    city: updatedAddress.city,
    state: updatedAddress.stateCode || '',
    postalCode: updatedAddress.postalCode,
    country: updatedAddress.countryCode,
  }
}

async function setAddressAsDefault(
  address: IAddress,
  isDefaultBilling: boolean,
  isDefaultShipping: boolean,
  isDefaultMailing: boolean,
) {
  try {
    const beeNumber = await getBeeNumber()
    if (!beeNumber) return

    const response = await PwsProfileAddressSave({
      beeNumber,
      addressId: address.addressId,
      address: {
        addressId: address.addressId,
        firstName: address.firstName,
        lastName: address.lastName,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        houseNumber: address.houseNumber,
        city: address.city,
        stateCode: address.stateCode,
        postalCode: address.postalCode,
        countryCode: address.countryCode,
      },
      isDefaultBilling,
      isDefaultShipping,
      isDefaultMailing,
      SkipAddressValidation: false,
    })

    if (response?.success || !response?.responseStatus?.errorCode) {
      showAddressSelection.value = false
      await Swal.fire({
        title: t('accountInfo.billingAddress.addressUpdated'),
        icon: 'success',
        confirmButtonText: t('buttons.Okay'),
      })
      await loadAddressList()
      return
    }

    await Swal.fire({
      title: t('labels.Error'),
      text: response?.message || response?.responseStatus?.message || t('accountInfo.billingAddress.updateError'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay'),
    })
  } catch (error) {
    logger.error('Address update failed', error)
    await handleError(error, t)
  }
}

async function selectExistingAddress(address: IAddress) {
  await setAddressAsDefault(
    address,
    addressSelectionType.value === 'billing',
    addressSelectionType.value === 'shipping',
    addressSelectionType.value === 'mailing',
  )
}

async function saveAddressRequest(
  beeNumber: number,
  addressId: number | undefined,
  skipAddressValidation: boolean,
): Promise<IPwsProfileAddressSaveResponse> {
  return PwsProfileAddressSave({
    beeNumber,
    addressId: addressId ?? null,
    address: {
      addressId: addressId,
      firstName: addressForm.value.firstName,
      lastName: addressForm.value.lastName,
      addressLine1: addressForm.value.address1,
      addressLine2: addressForm.value.address2 || undefined,
      houseNumber: addressForm.value.houseNumber || undefined,
      city: addressForm.value.city,
      stateCode: addressForm.value.state,
      postalCode: addressForm.value.postalCode,
      countryCode: addressForm.value.country,
    },
    isDefaultBilling: addressSelectionType.value === 'billing',
    isDefaultShipping: addressSelectionType.value === 'shipping',
    isDefaultMailing: addressSelectionType.value === 'mailing',
    SkipAddressValidation: skipAddressValidation,
  })
}

function setValidationPopupData(response: IPwsProfileAddressSaveResponse) {
  verifyAddress.value = (response.validAddresses || []).map((addr, index) => ({
    addressId: index + 1,
    addressLine1: addr.addressLine1,
    addressLine2: addr.addressLine2 || '',
    houseNumber: null,
    city: addr.city,
    stateCode: addr.stateCode,
    postalCode: addr.postalCode,
    countryCode: addr.countryCode,
    addressGoodDate: addr.addressGoodDate || new Date().toISOString(),
  }))
  addressVerifyError.value = true
  addressVerificationShow.value = true
}

async function saveAddress() {
  try {
    isSaving.value = true

    // Validate AddressControl fields first
    if (addressControlRef.value && !addressControlRef.value.validate()) {
      return
    }

    const schema = addressSchema(t, {
      firstName: addressForm.value.firstName?.trim() ?? '',
      address1: addressForm.value.address1?.trim() ?? '',
      city: addressForm.value.city?.trim() ?? '',
      postalCode: addressForm.value.postalCode?.trim() ?? '',
      country: addressForm.value.country?.trim() ?? '',
      state: addressForm.value.state?.trim(),
    })

    clearAddressErrors()
    if (!validateAddressAll(schema)) {
      const firstError = Object.values(addressErrors.value).find(error => !!error)
      await Swal.fire({
        title: t('labels.Error'),
        text: firstError ?? t('accountInfo.billingAddress.requiredFields'),
        icon: 'error',
        confirmButtonText: t('buttons.Okay'),
      })
      return
    }

    const beeNumber = await getBeeNumber()
    if (!beeNumber) return

    const existingAddressId = currentAddressId.value ?? undefined
    const shouldDetachSharedAddress =
      editingAddress.value &&
      isAddressIdSharedAcrossTypes(existingAddressId)

    const addressIdToSave = shouldDetachSharedAddress ? undefined : existingAddressId
    pendingSaveAddressId.value = addressIdToSave

    const response = await saveAddressRequest(beeNumber, addressIdToSave, false)

    if (response?.addressValidationMessageId) {
      setValidationPopupData(response)
      return
    }

    if (response?.success || !response?.responseStatus?.errorCode) {
      await Swal.fire({
        title: t('accountInfo.billingAddress.saveSuccess'),
        icon: 'success',
        confirmButtonText: t('buttons.Okay'),
      })
      showAddressForm.value = false
      editingAddress.value = false
      currentAddressId.value = null
      pendingSaveAddressId.value = undefined
      await loadAddressList()
      return
    }

    await Swal.fire({
      title: t('labels.Error'),
      text: response?.message || response?.responseStatus?.message || t('accountInfo.billingAddress.saveError'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay'),
    })
  } catch (error) {
    logger.error('Address save failed', error)
    await handleError(error, t)
  } finally {
    isSaving.value = false
  }
}

async function saveAddressWithSkipValidation() {
  try {
    const beeNumber = await getBeeNumber()
    if (!beeNumber) return

    const response = await saveAddressRequest(beeNumber, pendingSaveAddressId.value, true)

    if (response?.success || !response?.responseStatus?.errorCode) {
      await Swal.fire({
        title: t('accountInfo.billingAddress.saveSuccess'),
        icon: 'success',
        confirmButtonText: t('buttons.Okay'),
      })
      addressVerificationShow.value = false
      showAddressForm.value = false
      editingAddress.value = false
      currentAddressId.value = null
      pendingSaveAddressId.value = undefined
      await loadAddressList()
      return
    }

    await Swal.fire({
      title: t('labels.Error'),
      text: response?.message || response?.responseStatus?.message || t('accountInfo.billingAddress.saveError'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay'),
    })
  } catch (error) {
    logger.error('Address save with SkipAddressValidation failed', error)
    await handleError(error, t)
  }
}

function useEnteredAddressClick() {
  addressVerificationShow.value = false
  void saveAddressWithSkipValidation()
}

function useSelectedAddressClick(selectedAddress: IAddress) {
  addressForm.value = {
    ...addressForm.value,
    address1: selectedAddress.addressLine1 || '',
    address2: selectedAddress.addressLine2 || '',
    houseNumber: selectedAddress.houseNumber || addressForm.value.houseNumber,
    city: selectedAddress.city || '',
    state: selectedAddress.stateCode || '',
    postalCode: selectedAddress.postalCode || '',
    country: selectedAddress.countryCode || '',
  }

  addressVerificationShow.value = false
  void saveAddressWithSkipValidation()
}

function reEnterAddressClick() {
  addressVerificationShow.value = false
}

function cancelAddressForm() {
  showAddressForm.value = false
  editingAddress.value = false
  currentAddressId.value = null
  pendingSaveAddressId.value = undefined
  resetAddressForm()
}

async function deleteAddress(address: IAddress) {
  try {
    if (!address.addressId) {
      logger.error('Address ID is missing')
      return
    }

    const addressId = address.addressId
    const blockedTypes: string[] = []

    if (billingAddress.value?.addressId === addressId) {
      blockedTypes.push(getAddressTypeLabel('billing'))
    }
    if (shippingAddress.value?.addressId === addressId) {
      blockedTypes.push(getAddressTypeLabel('shipping'))
    }
    if (mailingAddress.value?.addressId === addressId) {
      blockedTypes.push(getAddressTypeLabel('mailing'))
    }

    if (blockedTypes.length > 0) {
      await Swal.fire({
        title: t('accountInfo.billingAddress.cannotDeleteTitle'),
        text: t('accountInfo.billingAddress.cannotDeleteMessage', { type: blockedTypes.join(', ') }),
        icon: 'warning',
        confirmButtonText: t('buttons.Okay'),
      })
      return
    }

    const result = await Swal.fire({
      title: t('accountInfo.billingAddress.deleteConfirmTitle'),
      text: t('accountInfo.billingAddress.deleteConfirmMessage'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('buttons.Yes'),
      cancelButtonText: t('buttons.No'),
    })

    if (!result.isConfirmed) {
      return
    }

    const beeNumber = await getBeeNumber()
    if (!beeNumber) return

    const response = await PwsProfileAddressDelete({
      beeNumber,
      addressId,
    })

    if (response?.success || !response?.responseStatus?.errorCode) {
      await Swal.fire({
        title: t('accountInfo.billingAddress.deleteSuccess'),
        icon: 'success',
        confirmButtonText: t('buttons.Okay'),
      })
      await loadAddressList()
      return
    }

    await Swal.fire({
      title: t('labels.Error'),
      text: response?.message || response?.responseStatus?.message || t('accountInfo.billingAddress.deleteError'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay'),
    })
  } catch (error) {
    logger.error('Address deletion failed', error)
    await handleError(error, t)
  }
}

async function reloadAddressData() {
  await loadAddressList()
}

onMounted(async () => {
  await loadAddressList()
})
</script>

<style lang="scss" scoped>
.ccma-address {
  padding: 1.5rem;
  width: 100%;
  position: relative;
}

.ccma-address-error {
  text-align: center;
  padding: 2rem;
  color: #dc3545;
}

/* Loading overlay styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(white, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-text {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #666;
}

/* Error notification styles */
.save-error-notification {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.error-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  gap: 0.75rem;
}

.error-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.error-details {
  flex: 1;
}

.error-details h3 {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 0.5rem 0;
  color: #721c24;
}

.error-details p {
  @include typography-body;
  margin: 0;
  color: #721c24;
}

.error-close {
  @include typography-h4;
  background: none;
  border: none;
  color: #721c24;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.error-close:hover {
  background-color: rgba(#721c24, 0.1);
}

.error-actions {
  padding: 0 1rem 1rem 1rem;
  border-top: 1px solid #f5c6cb;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

/* Success notification styles */
.save-success-notification {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.success-content {
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 0.75rem;
}

.success-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.success-message {
  flex: 1;
}

.success-message p {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #155724;
}

.success-close {
  @include typography-h4;
  background: none;
  border: none;
  color: #155724;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.success-close:hover {
  background-color: rgba(#155724, 0.1);
}

/* Animation for notifications */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.address-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 0.25rem;
}

.address-subtitle {
  @include typography-body;
  color: #6c757d;
}

.address-sections {
  margin-bottom: 2rem;
}

.address-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  background: var(--vt-c-white);
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.section-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--vt-c-white-soft);
}

.current-address {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.address-display {
  flex: 1;
}

.address-display p {
  white-space: pre-line;
  margin: 0;
  padding: 1rem;
  background-color: var(--vt-c-white-soft);
  @include typography-body;
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
}

.address-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 140px;
}

.no-address {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.no-address p {
  @include typography-body;
  margin-bottom: 1rem;
}

/* Address Form Modal */
.address-form-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(black, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-critical-backdrop;
}

.address-form-modal .modal-content {
  background: white;
  border-radius: $radius-md;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.address-form-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.address-form-modal .modal-header .form-title {
  @include typography-h4;
  @include font-weight-semibold;
  color: #333;
  margin: 0;
}

.address-form-modal .modal-body {
  padding: 1.5rem;
}

/* Form styles are now handled by AddressControl component */

.form-actions {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.75rem;
}

.baseBtn {
  padding: 0.5rem 1.5rem;
  @include typography-body;
  @include font-weight-medium;
  border: none;
  border-radius: $button-radius;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.baseBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


.secondaryBtn {
  background-color: #6c757d;
  color: white;
}

.secondaryBtn:hover:not(:disabled) {
  background-color: #545b62;
}

.outlineBtn {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #6c757d;
}

.outlineBtn:hover:not(:disabled) {
  background-color: #6c757d;
  color: white;
}

.me-2 {
  margin-right: 0.5rem;
}

/* Modal styles */
.existing-addresses-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(black, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-critical-backdrop;
}

.modal-content {
  background: white;
  border-radius: $radius-md;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  margin: 0;
  color: #333;
}

.modal-close {
  @include typography-h3;
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.modal-close:hover {
  background-color: var(--vt-c-white-soft);
}

.modal-body {
  padding: 1.5rem;
}

.no-addresses {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.address-item {
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.15s ease;
}

.address-item:hover {
  border-color: #007bff;
  background-color: var(--vt-c-white-soft);
}

.address-item p {
  @include typography-body;
  margin: 0;
  white-space: pre-line;
}

.address-management-view,
.address-selection-view,
.address-form-view {
  width: 100%;
}

.current-addresses {
  display: flex;
  flex-direction: column;
  gap: $spacing-6;
}

.selection-header,
.form-header {
  display: flex;
  align-items: center;
  gap: $gap-md;
  margin-bottom: $spacing-5;
}

.selection-header h3,
.form-header h3 {
  @include typography-h4;
  margin: 0;
}

.back-button {
  min-width: fit-content;
}

.address-content {
  flex: 1;
}

.address-name {
  @include typography-body;
  @include font-weight-semibold;
}

.address-line {
  @include typography-small;
}

.address-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $gap-md;
}

.address-item-actions {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
  min-width: 160px;
}

.address-item.add-new {
  justify-content: center;
  border-style: dashed;
}

.add-new-content {
  @include typography-body;
}

.address-section-title {
  @include typography-body-large;
  @include font-weight-semibold;
}

.ccma-address-card-layout {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $gap-md;
}

.ccma-address-card-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: $gap-sm;
  min-width: fit-content;
}

.ccma-address-card-action-btn {
  @include typography-small;
  padding: $button-padding-sm-y $button-padding-sm-x;
  width: auto;
  min-width: 190px;
}

.popup-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(black, 0.5);
  z-index: $z-modal-backdrop;
}

@media (max-width: 768px) {
  .ccma-address {
    padding: 1rem;
  }

  .address-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .ccma-address-card-layout {
    flex-direction: column;
    gap: $spacing-4;
  }

  .ccma-address-card-actions {
    width: 100%;
    align-items: stretch;
  }

  .ccma-address-card-action-btn {
    width: 100%;
    min-width: 0;
  }

  .current-address {
    flex-direction: column;
    gap: 1rem;
  }

  .address-actions:not(.ccma-address-card-actions) {
    flex-direction: row;
    min-width: auto;
  }

  .form-row {
    flex-direction: column;
    gap: 1rem;
  }

  .address-form-content {
    padding: 1.5rem 1rem;
  }

  .form-field {
    width: 100%;
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions .baseBtn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .ccma-address {
    padding: 0.5rem;
  }

  .address-form-content {
    padding: 1rem;
  }

  .address-section {
    padding: 1rem;
  }

  .form-row {
    gap: 0.75rem;
  }
}
</style>
